import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

import { setAccountId } from 'ducks/accountInfo/accountInfoSlice';
import type { Location } from 'react-router-dom';
import { useLocation } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';

export const PreviousLocationContext = createContext<Location | undefined>(
  undefined,
);

/*
  The PreviousLocationContext keeps track of the previous location.
*/
export function PreviousLocationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [prevLocation, setPrevLocation] = useState<Location>();
  const [currentLocation, setCurrentLocation] = useState<Location>();

  const location = useLocation();

  useEffect(() => {
    if (location.pathname === currentLocation?.pathname) {
      return;
    }

    setPrevLocation(currentLocation);
    setCurrentLocation(location);
  }, [currentLocation, location]);

  const accountId = useAppSelector(state => state.accountInfo.id);
  const dispatch = useAppDispatch();

  useEffect(() => {
    // set Account ID from URL in case of opening link
    const paths = location.pathname.split('/');
    const account = paths.length && paths[1];
    if (account && account !== accountId) {
      dispatch(setAccountId(account));
    }
  }, [accountId, dispatch, location.pathname]);

  return (
    <PreviousLocationContext.Provider value={prevLocation}>
      {children}
    </PreviousLocationContext.Provider>
  );
}

export const usePreviousLocation = () => useContext(PreviousLocationContext);
